<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import MdiArrowRight from '~icons/mdi/arrow-right'
import MdiTrashCan from '~icons/mdi/trash-can'
import MdiCheckCircle from '~icons/mdi/check-circle'
import MdiAlertCircle from '~icons/mdi/alert-circle'
import MdiLoading from '~icons/mdi/loading'
import MdiFileDocumentEditOutline from '~icons/mdi/file-document-edit-outline'

const props = defineProps({
  session: { type: Object, required: true }
})
const emit = defineEmits(['delete'])

const router = useRouter()
const { t } = useI18n()

const STATUS_CONFIG = computed(() => ({
  uploading: { label: t('import.statusUploading'), variant: 'badge-muted', icon: MdiLoading },
  parsing: { label: t('import.statusParsing'), variant: 'badge-muted', icon: MdiLoading },
  parsed: { label: t('import.statusParsed'), variant: 'badge-accent', icon: MdiFileDocumentEditOutline },
  mapping: { label: t('import.statusMapping'), variant: 'badge-accent', icon: MdiFileDocumentEditOutline },
  hierarchy: { label: t('import.statusHierarchy'), variant: 'badge-accent', icon: MdiFileDocumentEditOutline },
  reviewing: { label: t('import.statusReviewing'), variant: 'badge-accent', icon: MdiFileDocumentEditOutline },
  importing: { label: t('import.statusImporting'), variant: 'badge-muted', icon: MdiLoading },
  done: { label: t('import.statusDone'), variant: 'badge-success', icon: MdiCheckCircle },
  failed: { label: t('import.statusFailed'), variant: 'badge-danger', icon: MdiAlertCircle }
}))

const cfg = computed(() => STATUS_CONFIG.value[props.session.status] || STATUS_CONFIG.value.parsed)
const isDone = computed(() => props.session.status === 'done')
const isInProgress = computed(() => !['done', 'failed'].includes(props.session.status))
const isProcessing = computed(() =>
  ['uploading', 'parsing', 'importing'].includes(props.session.status)
)

const createdAt = computed(() => {
  const d = new Date(props.session.created_at)
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
})

function resume() {
  router.push(`/import/${props.session.id}`)
}
</script>

<template>
  <div class="data-card">
    <div class="data-card__main">
      <div class="flex items-center gap-2">
        <p class="data-card__name">{{ session.original_filename || $t('nav.import') }}</p>
        <span :class="['badge', cfg.variant, 'text-xs']">{{ cfg.label }}</span>
      </div>
      <div class="data-card__meta flex gap-3 flex-wrap">
        <span v-if="session.row_count != null">{{ $t('import.rowCount', { count: session.row_count }) }}</span>
        <span>{{ createdAt }}</span>
        <span v-if="session.created_by_name">{{ $t('import.createdBy', { name: session.created_by_name }) }}</span>
        <span v-if="isDone && session.result_summary">
          {{ $t('import.itemsAndInstances', { items: session.result_summary.items_created, instances: session.result_summary.instances_created }) }}
        </span>
      </div>
      <p v-if="session.error_message" class="text-red-400 text-xs mt-1">
        {{ session.error_message }}
      </p>
    </div>

    <div class="data-card__actions flex items-center gap-2">
      <button
        v-if="isInProgress && !isProcessing"
        class="btn btn-primary btn-small flex items-center gap-1"
        @click="resume"
      >
        {{ $t('import.continueImport') }}
        <MdiArrowRight class="w-4 h-4" />
      </button>
      <button
        v-else-if="isDone"
        class="btn btn-ghost btn-small flex items-center gap-1"
        @click="resume"
      >
        {{ $t('import.viewResults') }}
      </button>
      <button
        v-else-if="session.status === 'failed'"
        class="btn btn-ghost btn-small flex items-center gap-1"
        @click="resume"
      >
        {{ $t('import.reviewIssues') }}
      </button>
      <button
        class="btn btn-ghost btn-small text-red-400 hover:text-red-300"
        @click="emit('delete', session.id)"
      >
        <MdiTrashCan class="w-4 h-4" />
      </button>
    </div>
  </div>
</template>
